if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item']
        };

        this.initialized = false;
        this.sliderInstance = false;
        this.thumbsInstance = false;
      }

      connectedCallback() {
        this.prepareRegionGallery();
        this.addEventListener('click', this.onGalleryClick.bind(this), true);
        FoxTheme.Motion.inView(this, this.init.bind(this));

        if (window.FoxTheme && FoxTheme.pubsub) {
          this._depotUnsubscriber = FoxTheme.pubsub.subscribe(
            FoxTheme.pubsub.PUB_SUB_EVENTS.depotChange,
            () => this.prepareRegionGallery()
          );
        }
      }

      disconnectedCallback() {
        if (this._depotUnsubscriber) {
          this._depotUnsubscriber();
          this._depotUnsubscriber = undefined;
        }
      }

      init() {
        if (this.initialized === true) return;

        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.context = this.dataset.context;
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }

        this.initialized = true;
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);
        const mediaItemGapMobile = parseInt(this.dataset.mediaItemGapMobile);

        this.sliderOptions = {
          init: false,
          slidesPerView: this.enableMobileThumbnails ? 1 : 'auto',
          spaceBetween: mediaItemGapMobile,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap
            }
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev')
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction'
          },
          threshold: 2
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
              direction: 'horizontal'
            }
          },
          spaceBetween: mediaItemGapMobile,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
          breakpoints: {
            768: {
              spaceBetween: mediaItemGap
            }
          }
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              768: {
                direction: 'vertical',
                slidesPerView: 'auto',
                spaceBetween: mediaItemGap
              }
            };
            break;
          case 'carousel':
            this.thumbsOptions.breakpoints = {
              ...this.thumbsOptions.breakpoints,
              1024: {
                slidesPerView: 5
              },
              1536: {
                slidesPerView: 7
              }
            };
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (this.thumbsInstance && this.thumbsInstance.slider) {
          this.thumbsInstance.slider.destroy(true, true);
          this.thumbsInstance = false;
        }
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy(true, true);
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            const { mediaType, mediaIndex, src, pswpWidth, pswpHeight } = media.dataset;

            let source = {
              id: mediaIndex,
              mediaType: mediaType
            };

            switch (mediaType) {
              case 'model':
              case 'video':
              case 'external_video':
                const htmlTag = mediaType === 'model' ? 'product-model' : 'video-element';

                source = {
                  ...source,
                  html: `<div class="pswp__item--${mediaType}">${media.querySelector(htmlTag).outerHTML}</div>`
                };
                break;
              default: // Image.
                source = {
                  ...source,
                  src: src,
                  width: pswpWidth,
                  height: pswpHeight
                };
                break;
            }

            dataSource.push(source);
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
          // Don't restore focus to the opener thumbnail on close. The opener lives
          // inside the slide it was opened from, so when the tab is re-activated the
          // browser re-focuses (and scrolls) that element into view, hijacking the
          // swiper back to the opening index. Keeping focus off it prevents the jump.
          returnFocus: false
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          return this.querySelector(`.product__media-item[data-media-index="${id}"] img`) || thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data }) => {
          if (data.src) {
            return data.src;
          }
          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          const { currIndex } = this.lightbox?.pswp;
          const slider = this.sliderInstance?.slider;
          if (slider) {
            slider.slideTo(currIndex, 0, false);
          }
        });

        this.lightbox.on('destroy', () => {
          const { currIndex } = this.lightbox?.pswp;
          if (!this.sliderInstance && currIndex !== null) {
            const target = this.elements.mediaItems?.find((item) => Number(item.dataset.mediaIndex) === currIndex);
            if (target) {
              requestAnimationFrame(() => {
                const rect = target.getBoundingClientRect();
                const inView = rect.top >= 0 && rect.bottom <= window.innerHeight;
                if (!inView) {
                  target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              });
            }
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox?.pswp?.currSlide?.data?.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          const lightboxUI = this.lightbox?.pswp?.ui;

          if (!lightboxUI) return;

          if (!this.onlyImage) {
            lightboxUI.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.next();
              }
            });
            lightboxUI.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox?.pswp?.prev();
              }
            });
          }
          lightboxUI.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox?.pswp?.close();
            }
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset?.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          }
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);

            if (this._userSliding && !this._suppressVariantSelect) {
              this.selectVariantForMedia(slides[activeIndex].dataset.mediaId);
            }
          }
        });

        this.sliderInstance.slider.on('slideChangeTransitionEnd', () => {
          this._userSliding = false;
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant) return;

        const optionName = this.regionOptionName();
        const entry = this.readMediaMap().find((item) => String(item.variantId) === String(variant.id));
        const region = entry && entry.options.find((option) => option.name === optionName)?.value;
        if (region) this.prepareRegionGallery(region);

        let featuredMedia = variant.featured_media;

        if (!featuredMedia && variant.featured_image) {
          featuredMedia = {
            id: variant.featured_image.id,
            position: variant.featured_image.position || 0,
            media_type: 'image'
          };
        }

        if (!featuredMedia) return;

        if (this.sliderInstance && this.sliderInstance.slider) {
          const index = Array.from(this.sliderInstance.slider.slides).findIndex(
            (slide) => String(slide.dataset.mediaId) === String(featuredMedia.id)
          );
          if (index >= 0) {
            this._suppressVariantSelect = true;
            this.sliderInstance.slider.slideTo(index, 0, false);
            this._suppressVariantSelect = false;
          }
        } else {
          this.sortMediaItems(variant, featuredMedia);
        }
      }

      sortMediaItems(variant, featuredMedia) {
        if (!featuredMedia) {
          featuredMedia =
            variant.featured_media ||
            (variant.featured_image
              ? {
                  id: variant.featured_image.id,
                  position: variant.featured_image.position || 0,
                  media_type: 'image'
                }
              : null);
        }

        if (!featuredMedia) return;

        let newMedias = (this.elements.mediaItems || []).filter((media) => !media.hidden);

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == featuredMedia.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        if (!FoxTheme.config.mqlMobile && this.context !== 'quickview') {
          const selectedMedia = this.querySelector(`[data-media-id="${featuredMedia.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }

      regionOptionName() {
        return (window.MarketRegion && window.MarketRegion.OPTION_NAME) || 'Market Region';
      }

      readMediaMap() {
        if (this._mediaMap) return this._mediaMap;
        const node = this.querySelector('[data-region-media]');
        if (!node) {
          this._mediaMap = [];
          return this._mediaMap;
        }
        try {
          this._mediaMap = JSON.parse(node.textContent) || [];
        } catch (error) {
          this._mediaMap = [];
        }
        return this._mediaMap;
      }

      currentRegion() {
        const optionName = this.regionOptionName();
        const picker = this.closest('product-info')?.querySelector('variant-selects');
        if (picker) {
          const select = Array.from(picker.querySelectorAll('select')).find(
            (element) => element.name === `options[${optionName}]`
          );
          if (select) return select.value;

          const radio = Array.from(picker.querySelectorAll('input[type="radio"]')).find(
            (input) => input.checked && input.name.indexOf(`${optionName}-`) === 0
          );
          if (radio) return radio.value;
        }

        const depot = window.FoxTheme && window.FoxTheme.depot;
        if (window.MarketRegion && typeof window.MarketRegion.targetValueFor === 'function') {
          return window.MarketRegion.targetValueFor(depot);
        }
        return null;
      }

      mediaBelongsToRegion(mediaId, region) {
        const entries = this.readMediaMap().filter((entry) => String(entry.mediaId) === String(mediaId));
        if (!entries.length) return true;
        const optionName = this.regionOptionName();
        return entries.some((entry) =>
          entry.options.some((option) => option.name === optionName && option.value === region)
        );
      }

      /*
        Keep the slider to images for the current Market Region. Variant featured images are
        region-bound; media that no variant uses stays visible for every region.
      */
      prepareRegionGallery(region) {
        const map = this.readMediaMap();
        const optionName = this.regionOptionName();
        const hasRegion = map.some((entry) => entry.options.some((option) => option.name === optionName));
        if (!hasRegion) return;

        const nextRegion = region || this.currentRegion();
        if (!nextRegion || nextRegion === this._appliedRegion) return;
        this._appliedRegion = nextRegion;

        if (!this._viewerSlides) {
          const list = this.querySelector('[id^="Slider-Gallery"]');
          const thumbs = this.querySelector('[id^="Slider-Thumbnails"]');
          this._viewerList = list;
          this._thumbList = thumbs;
          this._viewerSlides = list ? Array.from(list.querySelectorAll(':scope > .product__media-item')) : [];
          this._thumbSlides = thumbs ? Array.from(thumbs.querySelectorAll(':scope > .product__thumbs-item')) : [];
          this._viewerSlides.sort((a, b) => Number(a.dataset.mediaIndex) - Number(b.dataset.mediaIndex));
          this._thumbSlides.sort((a, b) => Number(a.dataset.mediaIndex) - Number(b.dataset.mediaIndex));
          this._tray = document.createElement('div');
          this._tray.hidden = true;
          this._tray.setAttribute('data-region-tray', '');
          this.appendChild(this._tray);

          if (this.dataset.enableImageZoom !== 'true') {
            this._viewerSlides.forEach((slide) => {
              const selectable = map.some((entry) => String(entry.mediaId) === String(slide.dataset.mediaId));
              if (selectable) slide.classList.add('product__media-item--selectable');
            });
          }
        }

        const sliderWasLive = Boolean(this.sliderInstance && this.sliderInstance.slider);
        this._suppressVariantSelect = true;
        if (sliderWasLive) this.destroySlider();

        this._viewerSlides.forEach((slide) => {
          const show = this.mediaBelongsToRegion(slide.dataset.mediaId, nextRegion);
          slide.hidden = !show;
          (show ? this._viewerList : this._tray).appendChild(slide);
        });
        this._thumbSlides.forEach((slide) => {
          const show = this.mediaBelongsToRegion(slide.dataset.target, nextRegion);
          slide.hidden = !show;
          if (this._thumbList) (show ? this._thumbList : this._tray).appendChild(slide);
        });

        const visibleCount = this._viewerSlides.filter((slide) => !slide.hidden).length;
        this.classList.toggle('media-gallery--single', visibleCount < 2);

        if (sliderWasLive && this.initialized) this.updateMediaLayout();
        this._suppressVariantSelect = false;
      }

      onGalleryClick(event) {
        if (event.target.closest('.swiper-button-next, .swiper-button-prev, .swiper-pagination')) {
          this._userSliding = true;
          return;
        }

        if (event.target.closest('.js-photoswipe--zoom, button, a, video-element, product-model')) return;

        const thumb = event.target.closest('.product__thumbs-item');
        const slide = event.target.closest('.product__media-item');
        if (this.dataset.enableImageZoom === 'true' && slide && !thumb) return;

        const mediaId = thumb?.dataset.target || slide?.dataset.mediaId;
        if (!mediaId) return;
        this.selectVariantForMedia(mediaId);
      }

      selectVariantForMedia(mediaId) {
        const region = this._appliedRegion || this.currentRegion();
        const optionName = this.regionOptionName();
        let entries = this.readMediaMap().filter((entry) => String(entry.mediaId) === String(mediaId));
        if (!entries.length) return;
        if (region) {
          const inRegion = entries.filter((entry) =>
            entry.options.some((option) => option.name === optionName && option.value === region)
          );
          if (inRegion.length) entries = inRegion;
        }
        this.applyVariant(this.pickBestVariant(entries));
      }

      pickBestVariant(entries) {
        const picker = this.closest('product-info')?.querySelector('variant-selects');
        const optionName = this.regionOptionName();
        const current = {};

        if (picker) {
          picker.querySelectorAll('select').forEach((select) => {
            const match = select.name.match(/^options\[(.*)\]$/);
            if (match) current[match[1]] = select.value;
          });
          picker.querySelectorAll('input[type="radio"]:checked').forEach((input) => {
            current[input.name.replace(/-\d+$/, '')] = input.value;
          });
        }

        let best = entries[0];
        let bestScore = -1;
        entries.forEach((entry) => {
          let score = 0;
          entry.options.forEach((option) => {
            if (option.name === optionName) return;
            if (current[option.name] === option.value) score += 1;
          });
          if (score > bestScore) {
            best = entry;
            bestScore = score;
          }
        });
        return best;
      }

      applyVariant(entry) {
        const picker = this.closest('product-info')?.querySelector('variant-selects');
        if (!picker || !entry) return;
        if (this._pendingVariantId === entry.variantId) return;

        let changed = null;
        let alreadySelected = true;

        entry.options.forEach((option) => {
          const select = Array.from(picker.querySelectorAll('select')).find(
            (element) => element.name === `options[${option.name}]`
          );
          if (select) {
            if (select.value !== option.value) {
              alreadySelected = false;
              select.value = option.value;
              Array.from(select.options).forEach((item) => {
                if (item.value === option.value) item.setAttribute('selected', 'selected');
                else item.removeAttribute('selected');
              });
              changed = select;
            }
            return;
          }

          const radio = Array.from(picker.querySelectorAll('input[type="radio"]')).find(
            (input) => input.value === option.value && input.name.indexOf(`${option.name}-`) === 0
          );
          if (!radio || radio.checked) return;
          alreadySelected = false;
          radio.checked = true;
          changed = radio;
        });

        if (alreadySelected || !changed) return;

        this._pendingVariantId = entry.variantId;
        changed.dispatchEvent(new Event('change', { bubbles: true }));
        window.setTimeout(() => {
          if (this._pendingVariantId === entry.variantId) this._pendingVariantId = null;
        }, 1500);
      }
    }
  );
}
